import type { ReactNode } from 'react';

import { FlexAligner, Typography } from '@scaleaq/scaleui';

import { GroupBorder } from '@/styles.ts';
import { responsive } from '@/utils';

import { InfoGroupGrid } from './infoGroupCard.styles.ts';
import type { InfoGroupColumnCount, InfoGroupEntry } from './infoGroupCard.types.ts';
import { InfoGroupField } from './InfoGroupField.tsx';

export type { InfoGroupColumnCount, InfoGroupEntry };

type InfoGroupCardHeader = {
  title: string;
  /** Optional leading content (e.g. `<Info aria-hidden style={{ color: … }} />`); omit or `null` for title only. */
  icon?: ReactNode;
};

type InfoGroupCardProps = {
  /** Optional section title row (text + optional leading icon). */
  header?: InfoGroupCardHeader;
  entries: ReadonlyArray<InfoGroupEntry>;
  /** Grid column count. Default 3. */
  columns?: InfoGroupColumnCount;
  /** Optional trailing content (e.g. action buttons below the fact grid). */
  children?: ReactNode;
};

/** One titled group of key/value facts inside an information modal (e.g. overview, measurements, inventory). */
export const InfoGroupCard = ({ header, entries, columns = 3, children }: InfoGroupCardProps) => {
  const headerRow = header != null && header.title !== '' ? header : null;

  return (
    <GroupBorder>
      <FlexAligner direction='column' gap={responsive.spacing['2xlarge']} fullHeight={true}>
        {headerRow != null && (
          <FlexAligner direction='row' alignment='center-left' gap={responsive.spacing.medium}>
            {headerRow.icon != null && (
              <span
                style={{
                  display: 'flex',
                  flexShrink: 0,
                  alignItems: 'center',
                  lineHeight: 0,
                }}
              >
                {headerRow.icon}
              </span>
            )}
            <Typography type='body_large' weight={600}>
              {headerRow.title}
            </Typography>
          </FlexAligner>
        )}
        <InfoGroupGrid $columns={columns}>
          {entries.map((entry, index) => (
            <InfoGroupField key={entry.id ?? String(index)} {...entry} />
          ))}
        </InfoGroupGrid>
        {children}
      </FlexAligner>
    </GroupBorder>
  );
};
